// Compact temperature timeline. Draws individual readings, connects them
// with a thin line, paints the fever threshold (38 °C) as a dashed red
// reference, and shades any reading at/above it.

import type { Measurement } from '@shared/types.ts';
import { FEVER_THRESHOLD_C } from '@shared/constants.ts';
import { ageDaysAt } from '../../lib/measure.ts';

type Props = {
  readings: Measurement[]; // temperature kind only; any order
  birthDate?: string | null;
  /** Window in days. Default 14 — empty days collapse. */
  windowDays?: number;
};

export function TemperatureChart({ readings, birthDate, windowDays = 14 }: Props) {
  if (!readings.length) {
    return (
      <div
        style={{
          padding: '32px 16px',
          textAlign: 'center',
          color: 'var(--text-3)',
          fontSize: 13,
        }}
      >
        No temperatures logged yet.
      </div>
    );
  }

  const sorted = [...readings].sort((a, b) => a.occurred_at - b.occurred_at);
  const now = Math.floor(Date.now() / 1000);
  const xMaxSec = now;
  const xMinSec = Math.min(sorted[0].occurred_at, now - windowDays * 86400);
  const span = Math.max(1, xMaxSec - xMinSec);

  // y range: data + fever threshold reference, clamped to sensible bounds.
  const values = sorted.map((m) => m.value / 10);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const yLoRaw = Math.min(dataMin, 36);
  const yHiRaw = Math.max(dataMax, FEVER_THRESHOLD_C + 1);
  const yLo = Math.floor(yLoRaw - 0.2);
  const yHi = Math.ceil(yHiRaw + 0.2);

  const W = 340;
  const H = 180;
  const padL = 36;
  const padR = 12;
  const padT = 10;
  const padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const x = (sec: number) => padL + ((sec - xMinSec) / span) * innerW;
  const y = (v: number) => padT + (1 - (v - yLo) / Math.max(0.1, yHi - yLo)) * innerH;

  const linePath = sorted
    .map((m, i) => `${i === 0 ? 'M' : 'L'}${x(m.occurred_at).toFixed(1)},${y(m.value / 10).toFixed(1)}`)
    .join(' ');

  // y ticks every 1°C
  const yTicks: number[] = [];
  for (let v = Math.ceil(yLo); v <= Math.floor(yHi); v++) yTicks.push(v);

  // x ticks: 4 evenly-spaced day labels
  const days = Math.ceil((xMaxSec - xMinSec) / 86400);
  const stride = Math.max(1, Math.round(days / 4));
  const xTicks: number[] = [];
  for (let d = 0; d <= days; d += stride) xTicks.push(xMaxSec - d * 86400);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 200 }}>
      {/* Fever threshold band: y ≥ FEVER_THRESHOLD_C */}
      <rect
        x={padL}
        y={y(yHi)}
        width={innerW}
        height={Math.max(0, y(FEVER_THRESHOLD_C) - y(yHi))}
        fill="color-mix(in oklab, var(--danger) 8%, transparent)"
      />
      <line
        x1={padL}
        x2={W - padR}
        y1={y(FEVER_THRESHOLD_C)}
        y2={y(FEVER_THRESHOLD_C)}
        stroke="var(--danger)"
        strokeWidth={1}
        strokeDasharray="3 3"
        opacity={0.6}
      />
      <text
        x={W - padR}
        y={y(FEVER_THRESHOLD_C) - 4}
        textAnchor="end"
        fontFamily="Geist Mono, ui-monospace"
        fontSize={9}
        fill="var(--danger)"
        opacity={0.8}
      >
        fever ≥ {FEVER_THRESHOLD_C}°
      </text>

      {/* y gridlines + ticks */}
      {yTicks.map((t) => (
        <g key={`y-${t}`}>
          <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke="var(--divider)" strokeWidth={1} />
          <text
            x={padL - 6}
            y={y(t) + 3}
            textAnchor="end"
            fontFamily="Geist Mono, ui-monospace"
            fontSize={9}
            fill="var(--text-3)"
          >
            {t}°
          </text>
        </g>
      ))}

      {/* x ticks */}
      {xTicks.map((t) => {
        const d = new Date(t * 1000);
        const label =
          birthDate && Math.floor((now - t) / 86400) === 0
            ? 'today'
            : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        return (
          <g key={`x-${t}`}>
            <line
              x1={x(t)}
              x2={x(t)}
              y1={padT + innerH}
              y2={padT + innerH + 4}
              stroke="var(--text-3)"
              strokeWidth={1}
            />
            <text
              x={x(t)}
              y={padT + innerH + 16}
              textAnchor="middle"
              fontFamily="Geist Mono, ui-monospace"
              fontSize={9}
              fill="var(--text-3)"
            >
              {label}
            </text>
          </g>
        );
      })}

      {sorted.length > 1 && (
        <path
          d={linePath}
          fill="none"
          stroke="var(--danger)"
          strokeOpacity={0.5}
          strokeWidth={1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {sorted.map((m, i) => {
        const v = m.value / 10;
        const fever = v >= FEVER_THRESHOLD_C;
        return (
          <circle
            key={m.id}
            cx={x(m.occurred_at)}
            cy={y(v)}
            r={i === sorted.length - 1 ? 4.5 : 3}
            fill={fever ? 'var(--danger)' : 'var(--text-2)'}
            stroke="var(--bg-card)"
            strokeWidth={1.2}
          />
        );
      })}
    </svg>
  );
}
